// ============================================================
// TokenSocials — X / Telegram / Website as click-through icons.
// ------------------------------------------------------------
// ONE definition, two homes: the scan cards and the footer token card. They
// want the same three icons behaving the same way, and a second copy would
// drift the moment either one got a fourth link.
//
// THE DATA WAS ALREADY THERE. Jupiter's tokens/v2 response carries `twitter`,
// `telegram` and `website` as top-level fields, right next to `icon`, `mcap`
// and `launchpad` — all of which the scan card already reads. ScanPanel does
// `setScanTokens(json)` with the raw objects, so these have been arriving on
// every 30s refresh and being thrown away. No new fetch, no new dependency, no
// rate limit.
//
// THE ONE THING THAT MATTERS HERE: the scan card ROW is itself a click target
// (`onClick={() => onSelect(addr)}` — it charts the token). An <a> inside it
// would do BOTH: open the link AND chart the coin behind it. Every handler
// below stops propagation. That's not defensive, it's the entire reason this
// isn't three inline <a> tags.
// ============================================================

// Jupiter returns these inconsistently — sometimes a bare handle ("@foo" or
// "foo"), sometimes a full URL, sometimes a t.me/x.com link with no scheme.
// Normalise to something a browser will actually navigate to, and refuse
// anything that isn't http(s) once resolved.
function href(kind, raw) {
  const v = String(raw || '').trim()
  if (!v) return null

  if (/^https?:\/\//i.test(v)) return v
  if (/^www\./i.test(v)) return `https://${v}`

  const handle = v.replace(/^@/, '')
  if (!handle) return null

  if (kind === 'twitter') {
    // A bare handle, or a x.com/twitter.com path with the scheme missing.
    if (/^(x|twitter)\.com\//i.test(handle)) return `https://${handle}`
    return `https://x.com/${handle}`
  }
  if (kind === 'telegram') {
    if (/^t\.me\//i.test(handle)) return `https://${handle}`
    return `https://t.me/${handle}`
  }
  // website: anything left that looks like a domain
  if (/\.[a-z]{2,}/i.test(handle)) return `https://${handle}`
  return null
}

const ICONS = {
  twitter: (
    <path d="M18.9 2H22l-7.3 8.3L23 22h-6.8l-5.3-6.9L4.8 22H1.7l7.8-8.9L1 2h7l4.8 6.3L18.9 2Zm-1.1 18h1.7L7.3 3.7H5.5l12.3 16.3Z" />
  ),
  telegram: (
    <path d="M21.9 4.3 18.6 20c-.2 1.1-.9 1.3-1.8.8l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.4-5 9.1-8.2c.4-.4-.1-.6-.6-.2L6.2 13.5l-4.8-1.5c-1-.3-1.1-1 .2-1.5l18.9-7.3c.9-.3 1.6.2 1.4 1.1Z" />
  ),
  website: (
    <>
      <circle cx="12" cy="12" r="9.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <ellipse cx="12" cy="12" rx="4" ry="9.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <line x1="2.9" y1="12" x2="21.1" y2="12" stroke="currentColor" strokeWidth="1.7" />
    </>
  ),
}
const LABELS = { twitter: 'X / Twitter', telegram: 'Telegram', website: 'Website' }

/**
 * @param token  any object carrying { twitter, telegram, website } — a raw
 *               Jupiter token works as-is.
 * @param size   icon px (12 in a scan card, 15 in the token card)
 */
export default function TokenSocials({ token, size = 12, className = '' }) {
  if (!token) return null

  const links = ['twitter', 'telegram', 'website']
    .map(kind => ({ kind, url: href(kind, token[kind]) }))
    .filter(l => l.url)

  // No links = no row. The scan list is dense; an empty strip of nothing on
  // every card that happens to have no socials is worse than no strip at all.
  if (!links.length) return null

  return (
    <div
      className={`tk-socials ${className}`}
      // Belt AND braces. The row is the click target; without this a tap that
      // LANDS on the gap between two icons still charts the token, which feels
      // like a misfire even though it technically isn't.
      onClick={(e) => e.stopPropagation()}
    >
      <style>{`
        .tk-socials { display: inline-flex; align-items: center; gap: 7px; }
        .tk-socials a {
          display: inline-flex; align-items: center; justify-content: center;
          color: var(--text-lo, #5a6172);
          opacity: 0.75;
          transition: color 0.12s, opacity 0.12s, transform 0.12s;
          /* A 12px glyph is a 12px tap target, which is not a tap target. Pad it
             out to something a thumb can actually hit without growing the row. */
          padding: 3px;
          margin: -3px;
          line-height: 0;
        }
        .tk-socials a:hover {
          color: var(--accent, #f0a72b);
          opacity: 1;
          transform: translateY(-1px);
        }
        .tk-socials svg { display: block; fill: currentColor; }
      `}</style>

      {links.map(({ kind, url }) => (
        <a
          key={kind}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          title={LABELS[kind]}
          aria-label={LABELS[kind]}
          // The row's onClick charts the token. Without this, tapping X would
          // open the link AND swap the chart out from under you.
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
            {ICONS[kind]}
          </svg>
        </a>
      ))}
    </div>
  )
}
